import axios from 'axios';
import { createContext, useContext, useCallback } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';

const ThematicApiContext = createContext(null);

export function ThematicApiProvider({ children }) {
  const { get, post, put, del, postUpload } = useApiService(CONFIG.SERVER_CHANNEL);

  const getThematic = useCallback(
    async (skip, limit) => {
      if (!skip) {
        skip = 0;
      }
      if (!limit) {
        limit = 10;
      }
      try {
        const response = await get(`/v1/thematics?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching thematics:', error);
        throw error;
      }
    },
    [get],
  );

  const getThematicById = useCallback(
    async (thematicId) => {
      try {
        const response = await get(`/v1/thematics/${thematicId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching thematic by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const createThematic = useCallback(
    async (data) => {
      try {
        const response = await post(`/v1/thematics`, data);
        return response.data;
      } catch (error) {
        console.error('Error creating thematic:', error);
        throw error;
      }
    },
    [put],
  );

  const updateThematic = useCallback(
    async (thematicId, data) => {
      try {
        const response = await put(`/v1/thematics/${thematicId}`, data);
        return response.data;
      } catch (error) {
        console.error('Error updating thematic:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteThematic = useCallback(
    async (thematicId) => {
      try {
        const response = await del(`/v1/thematics/${thematicId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting thematic:', error);
        throw error;
      }
    },
    [del],
  );

  const bulkDeleteThematics = useCallback(
    async (keys) => {
      try {
        const response = await post(`/v1/thematics/bulk-delete`, keys);
        return response.data;
      } catch (error) {
        console.error('Error bulk deleting thematics:', error);
        throw error;
      }
    },
    [post],
  );

  const importThematics = useCallback(
    async (file) => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await postUpload(`/v1/thematics/import`, formData);
        return response.data;
      } catch (error) {
        const serverMessage = axios.isAxiosError(error)
          ? (error.response?.data?.error ?? error.response?.data?.message)
          : undefined;
        console.error('Error importing thematics:', serverMessage ?? error);
        throw error;
      }
    },
    [postUpload],
  );

  const exportThematics = useCallback(
    async (params = {}) => {
      try {
        const response = await post('/v1/thematics/export', params.keys, {
          responseType: 'blob',
        });

        return response.data;
      } catch (error) {
        console.error('Error exporting thematics:', error);
        throw error;
      }
    },
    [post],
  );

  return (
    <ThematicApiContext.Provider
      value={{
        getThematic,
        getThematicById,
        createThematic,
        updateThematic,
        deleteThematic,
        bulkDeleteThematics,
        importThematics,
        exportThematics,
      }}
    >
      {children}
    </ThematicApiContext.Provider>
  );
}

export function useThematicApi() {
  const context = useContext(ThematicApiContext);
  if (!context) {
    throw new Error('useThematicApi must be used within an ThematicApiContext');
  }
  return context;
}
